// ThirdPartyClaimDetails.tsx
import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { ClaimDetail, ThirdPartyClaimDetailsProps } from "../../Interfaces/interfaces";
import { apiService } from "../../../../api/api";

const ThirdPartyClaimDetails: React.FC<ThirdPartyClaimDetailsProps> = ({
  rowData,
  visible,
  onHide,
}) => {
  const [claimDetails, setClaimDetails] = useState<ClaimDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !rowData?.videoId) {
      setClaimDetails([]);
      setError(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Prefer inline payload when present; otherwise fetch on demand (list API omits blobs).
        let raw = rowData.thirdPartyClaims;
        if (!raw) {
          const details =
            await apiService.youtubeScorecard.getYouTubeScorecardClaimsAdsClaimDetails(
              rowData.videoId
            );
          raw = details.thirdPartyClaims;
        }

        if (cancelled) return;

        if (!raw) {
          setClaimDetails([]);
          return;
        }

        const claims = typeof raw === "string" ? JSON.parse(raw) : raw;
        setClaimDetails(
          Array.isArray(claims) && claims.length > 0 ? claims : []
        );
      } catch (err) {
        console.error("Error loading thirdPartyClaims:", err);
        if (!cancelled) {
          setClaimDetails([]);
          setError("Failed to load claim details");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [rowData, visible]);

  return (
    <Dialog
      header={`Details for: '${rowData?.videoTitle || ""}'`}
      visible={visible}
      onHide={onHide}
      closable
      style={{
        minWidth: "90vw",
        width: "auto",
        maxWidth: "1200px",
        minHeight: "10vw",
        height: "auto",
        maxHeight: "90vh",
      }}
      contentStyle={{ overflow: "auto" }}
    >
      <div className="w-full">
        <h2 className="text-center text-2xl mb-4">Third Party Claim Details</h2>

        {loading ? (
          <div className="flex justify-content-center p-4">
            <ProgressSpinner style={{ width: "40px", height: "40px" }} />
          </div>
        ) : error ? (
          <p>{error}</p>
        ) : claimDetails.length > 0 ? (
          <table
            className="table-auto w-full text-sm"
            style={{ paddingBottom: "2vw" }}
          >
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th className="border px-4 py-2">Asset ID</th>
                <th className="border px-4 py-2">Time Created</th>
                <th className="border px-4 py-2">Time Last Modified</th>
                <th className="border px-4 py-2">Content Type</th>
                <th className="border px-4 py-2">Claim Status</th>
                <th className="border px-4 py-2">Owner Name</th>
                <th className="border px-4 py-2">
                  Conflict Notification Email
                </th>
              </tr>
            </thead>
            <tbody>
              {claimDetails.map((claim, index) => (
                <tr key={index}>
                  <td className="border px-4 py-2">{claim.assetId}</td>
                  <td className="border px-4 py-2">{claim.timeCreated}</td>
                  <td className="border px-4 py-2">
                    {claim.timeStatusLastModified}
                  </td>
                  <td className="border px-4 py-2">{claim.contentType}</td>
                  <td className="border px-4 py-2">{claim.claimStatus}</td>
                  <td className="border px-4 py-2">{claim.ownerName}</td>
                  <td className="border px-4 py-2">
                    {claim.conflictNotificationEmail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No claim data available.</p>
        )}
      </div>
    </Dialog>
  );
};

export default ThirdPartyClaimDetails;
